import { smoothstep } from '../helpers.js';

import gravitVertex from '../../../shaders/gravity/vertexGravity.glsl';
import gravitFragment from '../../../shaders/gravity/fragmentGravity.glsl';
import { sceneManager } from '../script.js';
import {
  Vector3,
  BufferGeometry,
  Float32BufferAttribute,
  ShaderMaterial,
  Color,
  AdditiveBlending,
  LineSegments,
} from 'three';

function generateGridData(size, divisions, params, object, sagitarius) {
  const positions = [];
  const colors = [];
  const alphas = [];
  const basePositions = [];

  const step = (2 * size) / divisions;

  const meshPos = new Vector3();

  if(sagitarius){
   sagitarius.mesh.getWorldPosition(meshPos);
   meshPos.negate();
  }else{
  object.mesh.getWorldPosition(meshPos);
  }
  

  const originIndex = new Vector3(
    Math.floor(meshPos.x / step),
    Math.floor(meshPos.y / step),
    Math.floor(meshPos.z / step)
  );
  const originWorld = originIndex.clone().multiplyScalar(step);

  const half = size;

  const scaleFactor = params.distortionScale * (10 / Math.max(1, divisions));

  function radialDistortionToMesh_world(posWorld) {
    const center = meshPos.clone(); 
    const dir = posWorld.clone().sub(center);
    const r = dir.length();
    if (r === 0) return center.clone();
    const minDist = params.bodyRadius / 2;
    const rFactor = smoothstep(minDist, params.bodyRadius * params.distortionRangeFactor, r);
    const totalDist = 10 * rFactor * scaleFactor * params.bodyRadius;
    const newDist = Math.max(r - totalDist, 0);
    return center.clone().add(dir.normalize().multiplyScalar(newDist));
  }

  function computeColorAlpha_world(distortedWorld) {
    const r = distortedWorld.distanceTo(meshPos);
    const minDist = params.bodyRadius / 2;
    let u = (r - minDist) / (size - minDist);
    u = Math.max(0, Math.min(u, 1));
    let t = 1 - smoothstep(0, 1, u);
    return { color: new Color(t, t, 1 - t), alpha: 0.15 + 0.85 * t };
  }

  function getEdgeDivisionsForSegment(midWorld, distRot) {
    const norm = Math.max(
      Math.abs(midWorld.x - meshPos.x),
      Math.abs(midWorld.y - meshPos.y),
      Math.abs(midWorld.z - meshPos.z)
    ) / size;
    const nPoints = Math.floor(Math.max(1, distRot) * 6);
    return norm <= 0.5 ? nPoints : 2;
  }
  
  const addLineSegment = (v1World, v2World) => {
    const midWorld = v1World.clone().add(v2World).multiplyScalar(0.5);
    const edgeDivs = getEdgeDivisionsForSegment(midWorld, object.distRot || 1);
    for (let s = 0; s < edgeDivs; s++) {
      const t1 = s / edgeDivs;
      const t2 = (s + 1) / edgeDivs;
      const segStartWorld = new Vector3().lerpVectors(v1World, v2World, t1);
      const segEndWorld = new Vector3().lerpVectors(v1World, v2World, t2);

      const d1World = radialDistortionToMesh_world(segStartWorld);
      const d2World = radialDistortionToMesh_world(segEndWorld);

      const d1Local = d1World.clone().sub(meshPos);
      const d2Local = d2World.clone().sub(meshPos);

      basePositions.push(d1Local.clone());
      basePositions.push(d2Local.clone());

      positions.push(d1Local.x, d1Local.y, d1Local.z);
      positions.push(d2Local.x, d2Local.y, d2Local.z);

      const ca1 = computeColorAlpha_world(d1World);
      const ca2 = computeColorAlpha_world(d2World);
      colors.push(ca1.color.r, ca1.color.g, ca1.color.b);
      colors.push(ca2.color.r, ca2.color.g, ca2.color.b);
      alphas.push(ca1.alpha, ca2.alpha);
    }
  };

  for (let axis = 0; axis < 3; axis++) {
    const fixed = [];
    for (let a = 0; a < 3; a++) if (a !== axis) fixed.push(a);

    const coordsFixed0 = [];
    const coordsFixed1 = [];
    for (let i = 0; i <= divisions; i++) {
      coordsFixed0.push(originWorld.getComponent(fixed[0]) - half + i * step);
      coordsFixed1.push(originWorld.getComponent(fixed[1]) - half + i * step);
    }

    for (let i = 0; i <= divisions; i++) {
      for (let j = 0; j <= divisions; j++) {
        for (let k = 0; k < divisions; k++) {
          const v1World = new Vector3();
          const v2World = new Vector3();

          v1World.setComponent(axis, originWorld.getComponent(axis) - half + k * step);
          v2World.setComponent(axis, originWorld.getComponent(axis) - half + (k + 1) * step);

          v1World.setComponent(fixed[0], coordsFixed0[i]);
          v1World.setComponent(fixed[1], coordsFixed1[j]);
          v2World.setComponent(fixed[0], coordsFixed0[i]);
          v2World.setComponent(fixed[1], coordsFixed1[j]);

          addLineSegment(v1World, v2World);
        }
      }
    }
  }

  return { positions, colors, alphas, basePositions };
}

export function createAstroGrid(object) {
  const astroParams = {
    bodyRadius: object.size,
    gridFactor: 25,
    distortionRangeFactor: 2,
    distortionScale: object.distScale,
    axialTilt: object.axialTilt,
    distRot: object.distRot
  };

  const gridSize = object.size * astroParams.gridFactor;
  const divisions = 5;

  const { positions, colors, alphas, basePositions } = generateGridData(gridSize, divisions, astroParams, object);

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
  geometry.setAttribute('color', new Float32BufferAttribute(colors, 3));
  geometry.setAttribute('alpha', new Float32BufferAttribute(alphas, 1));
  geometry.userData.basePositions = basePositions;

  const material = new ShaderMaterial({
    uniforms: { uFade: { value: 1.0 } },
    vertexShader: gravitVertex,
    fragmentShader: gravitFragment,
    transparent: true,
    vertexColors: true,
    blending: AdditiveBlending,
    depthWrite: false,
    depthTest: false
  });

  const grid = new LineSegments(geometry, material);
  grid.renderOrder = 2;
  sceneManager.scene.add(grid);
  return grid;
}

export function updateAstroGrid(grid, object, distance, sagitarius) {
  const astroParams = {
    bodyRadius: object.size,
    gridFactor: 25,
    distortionRangeFactor: 2,
    distortionScale: object.distScale,
    axialTilt: object.axialTilt,
    distRot: object.distRot
  };
  const gridSize = object.size * astroParams.gridFactor;
  const divisions = 5;

  const { positions, colors, alphas, basePositions } =
    generateGridData(gridSize, divisions, astroParams, object, sagitarius);

  const geo = grid.geometry;
  const posAttr = geo.attributes.position;
  const colAttr = geo.attributes.color;
  const alphaAttr = geo.attributes.alpha;
  
  const needsRebuild =
    positions.length !== posAttr.array.length ||
    colors.length !== colAttr.array.length ||
    alphas.length !== alphaAttr.array.length;

  if (needsRebuild) {
    geo.dispose();

    const newGeo = new BufferGeometry();
    newGeo.setAttribute('position', new Float32BufferAttribute(positions, 3));
    newGeo.setAttribute('color', new Float32BufferAttribute(colors, 3));
    newGeo.setAttribute('alpha', new Float32BufferAttribute(alphas, 1));
    newGeo.userData.basePositions = basePositions;

    grid.geometry = newGeo;
  } else {

    for (let i = 0; i < positions.length; i++) posAttr.array[i] = positions[i];
    for (let i = 0; i < colors.length; i++) colAttr.array[i] = colors[i];
    for (let i = 0; i < alphas.length; i++) alphaAttr.array[i] = alphas[i];

    posAttr.needsUpdate = true;
    colAttr.needsUpdate = true;
    alphaAttr.needsUpdate = true;

    grid.geometry.userData.basePositions = basePositions;
  }

  const tiltRadians = astroParams.axialTilt * Math.PI / 180;
  const rotationAxis = new Vector3(0, Math.cos(tiltRadians), Math.sin(tiltRadians)).normalize();
  const posArray = grid.geometry.attributes.position.array;
  const bps = grid.geometry.userData.basePositions || basePositions;

  for (let i = 0; i < bps.length; i++) {
    const bp = bps[i].clone();
    const r = bp.length();
    const damping = Math.exp(-r / gridSize * 50);
    const angle = object.distRot * damping;
    bp.applyAxisAngle(rotationAxis, angle);

    posArray[i * 3] = bp.x;
    posArray[i * 3 + 1] = bp.y;
    posArray[i * 3 + 2] = bp.z;
  }
  grid.geometry.attributes.position.needsUpdate = true;

  const fadeStart = object.size * 10;
  const fadeEnd = object.size * 200;
  let fade = 1 - (distance - fadeStart) / (fadeEnd - fadeStart);
  grid.material.uniforms.uFade.value = fade
}

export function setBodyGrid(grid, object, controls, computedDistance, sagitarius) {
  const centerGrid = new Vector3();
  object.mesh.getWorldPosition(centerGrid);
  grid.position.copy(centerGrid);
  grid.visible = controls.spaceTimeGrid;

  if(object.name.toLowerCase() === 'sun'){

  updateAstroGrid(grid, object, computedDistance, sagitarius);
  }else{

  updateAstroGrid(grid, object, computedDistance);
  }

}
